/**
 * React Query Hooks for Programs API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { programsApi } from '../../api/programs';
import type { CreateProgramRequest, UpdateProgramRequest } from '../../api/programs';
import { queryKeys } from '../../lib/queryClient';

/**
 * Fetch all programs
 */
export function usePrograms() {
  return useQuery({
    queryKey: queryKeys.programs.lists(),
    queryFn: () => programsApi.list(),
  });
}

/**
 * Fetch single program by ID
 */
export function useProgram(programId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.programs.detail(programId!),
    queryFn: () => programsApi.get(programId!),
    enabled: !!programId, // Only run if programId exists
  });
}

/**
 * Fetch program admins
 */
export function useProgramAdmins(programId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.programs.admins(programId!),
    queryFn: () => programsApi.getAdmins(programId!),
    enabled: !!programId,
  });
}

/**
 * Create new program
 */
export function useCreateProgram() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateProgramRequest) => programsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

/**
 * Update existing program
 */
export function useUpdateProgram() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ programId, data }: { programId: string; data: UpdateProgramRequest }) =>
      programsApi.update(programId, data),
    onSuccess: (_, variables) => {
      // Invalidate specific program and list
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.detail(variables.programId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.lists() });
    },
  });
}

/**
 * Delete program
 */
export function useDeleteProgram() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (programId: string) => programsApi.delete(programId),
    onSuccess: () => {
      // Invalidate programs list
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.lists() });
    },
  });
}

/**
 * Add program admin
 */
export function useAddProgramAdmin() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ programId, userId, alias, name, email }: { programId: string; userId: string; alias: string; name?: string; email?: string }) =>
      programsApi.addAdmin(programId, userId, alias, name, email),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.admins(variables.programId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.detail(variables.programId) });
      // Invalidate permissions for the newly added admin so their UI updates immediately
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Remove program admin
 */
export function useRemoveProgramAdmin() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ programId, userId }: { programId: string; userId: string }) =>
      programsApi.removeAdmin(programId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.admins(variables.programId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.detail(variables.programId) });
      // Invalidate permissions for the removed admin so their UI updates immediately
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Transfer program ownership
 */
export function useTransferProgramOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      programId,
      newOwnerId,
      newOwnerName,
      retainAsAdmin = true,
    }: {
      programId: string;
      newOwnerId: string;
      newOwnerName: string;
      retainAsAdmin?: boolean;
    }) => programsApi.transferOwnership(programId, newOwnerId, newOwnerName, retainAsAdmin),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.detail(variables.programId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.admins(variables.programId) });
    },
  });
}

/**
 * Publish a program (one-way, idempotent)
 */
export function usePublishProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (programId: string) => programsApi.publish(programId),
    onSuccess: (_, programId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.detail(programId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.programs.lists() });
    },
  });
}
